import { FormEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';
import { TFunction } from 'i18next';

import { ErrorsNavigationContextProvider } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContextProvider';
import { useFlattenDataAndUIConfig } from 'hooks/useFlattenDataAndUIConfig';
import { useIssueResolver } from 'hooks/useIssueResolver';
import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';
import { useInitWebEDIStore } from 'services/webedi/webedi';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { useFocusErrorOnMount } from 'services/webediErrors/hooks';
import {
  setDestinationProcess,
  setInitialCanonical,
  setIsValidating,
  setProcessSpecificationFields,
  useWebEDIStore,
} from 'stores/webedi/webedi';
import { DATA_CHECK_ERROR_CODE, EMPTY_FIELD, EMPTY_FUNCTION } from 'support/helpers/const/const';
import { routeToPage, routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { isCurrentPartnerTheMessageReceiver } from 'support/helpers/relationships/relationships';
import { datachecksResolver } from 'support/helpers/resolvers/datachecksResolver';
import { RouteParams } from 'support/helpers/types/types';
import {
  DocumentType,
  DocumentTypeWithVirtual,
  IssueResolverType,
  MessageState,
  MessageV2DTO,
  PartnerDTO,
  ProcessSpecificationDTO,
  TransactionInternalType,
  TransactionOriginApp,
  TransactionsRoutes,
  UserDTO,
} from 'support/types';
import { DisplayModes } from 'components/DataTable/Types';
import { Form } from 'components/Form/Form';
import { LoadingOverlay } from 'components/Loading/LoadingOverlay';
import { UnsavedChangesModal } from 'components/Navigation/UnsavedChangesModal/UnsavedChangesModal';
import { Page, PAGE_SECTION_PORTAL_ROOT_ID } from 'components/Page/Page';
import { Placeholder } from 'components/Placeholder/Placeholder';
import { DocumentSections } from 'components/WebEDI/DocumentSections/DocumentSections';
import { MessageFieldErrorModal } from 'components/WebEDI/MessageFieldErrorModal/MessageFieldErrorModal';
import { WebEDIMessageErrorBanner } from 'components/WebEDI/WebEDIMessageErrorBanner/WebEDIMessageErrorBanner';
import { PDFReferenceSidebar } from 'components/WebEDI/WebEDISidebar/components/PDFReferenceSidebar';

import { useEditMessage } from '../hooks/useEditMessage';
import { SendConfirmationModal } from '../components/CreateDocument/components/SendConfirmationModal/SendConfirmationModal';
import { Datachecks } from '../components/Datachecks/Datachecks';
import { CreateEditDocumentBottomBanner } from '../CreateEditDocumentBottomBanner';
import { revealPartyFields } from '../helpers';
import { MessageHeader } from '../TransactionViewPage/MessageViewPage/MessageHeader/MessageHeader';
import { UnknownTypePlaceholder } from '../TransactionViewPage/MessageViewPage/UnknownTypePlaceholder';

const DISPLAY_MODE = DisplayModes.edit;
export function EditDocument() {
  useInitWebEDIStore();
  const { id } = useParams<{ id: string }>();
  const { data: currentPartner } = useCurrentPartner();
  const { data: currentUser } = useCurrentUser();
  const { data: message, isError } = useMessageWithProcessSpecification({
    variables: {
      messageId: id,
    },
  });

  const fields = useMemo(() => {
    if (!message?.fields || !currentUser?.isAdmin) {
      return message?.fields;
    }
    return revealPartyFields(message.fields);
  }, [message?.fields, currentUser?.isAdmin]);

  const {
    data,
    uiConfig,
    isLoading: isLoadingDataChecks,
  } = useFlattenDataAndUIConfig(fields, message?.data?.canonical);

  useEffect(() => {
    setProcessSpecificationFields(fields);

    if (message?.data.canonical) {
      setInitialCanonical(message?.data.canonical);
    }

    if (message?.process) {
      setDestinationProcess(message?.process);
    }
  }, [message, fields]);

  if (!id) {
    return;
  }

  if (isError) {
    return <UnknownTypePlaceholder messageId={id} />;
  }

  if (isLoadingDataChecks) {
    /*
     * We don't return MessageViewPageUI so we can start the form with the correct initial data
     * This way we avoid rendering "0 line items" in the UI when the data is loaded but not yet debounced
     */
    return <Page isLoading={isLoadingDataChecks}>{null}</Page>;
  }

  return (
    <EditDocumentPageUI
      message={message?.data}
      messageOriginApp={message?.origin_app}
      data={data}
      relatedTransactions={message?.related_messages || []}
      currentPartner={currentPartner}
      uiConfig={uiConfig}
      processSpecFields={fields}
      currentUser={currentUser}
    />
  );
}

type EditDocumentPageUIProps = {
  message: MessageV2DTO | undefined;
  messageOriginApp: TransactionOriginApp | undefined;
  data: Record<string, unknown>;
  relatedTransactions: Array<MessageV2DTO>;
  currentPartner: PartnerDTO | undefined;
  uiConfig: Array<ui.Section> | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
  currentUser: UserDTO | undefined;
};

const useEditDocumentForm = ({
  message,
  processSpecFields,
  data,
}: {
  message: MessageV2DTO | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
  data: Record<string, unknown>;
}) => {
  const isBigMessage = useWebEDIStore((state) => state.isBigMessage);
  const isValidationComplete = useWebEDIStore((state) => state.isValidationComplete);
  const shouldRemoveResolver = isBigMessage && isValidationComplete;
  const hasDatachecksError = message?.blocking_error?.code === DATA_CHECK_ERROR_CODE;

  const resolver = useMemo(
    () =>
      message?.internal_state === MessageState.routingFailed || shouldRemoveResolver
        ? undefined
        : datachecksResolver({ fieldsConfiguration: processSpecFields, withTimeout: false }),
    [message?.internal_state, processSpecFields, shouldRemoveResolver],
  );

  const formMethods = useForm({
    defaultValues: data,
    values: data,
    resolver,
    mode: 'onSubmit',
    ...(isBigMessage && { reValidateMode: 'onSubmit' }),
  });

  useEffect(() => {
    /*
     * If there is a data checks error, we need to trigger the validation to show the errors
     */
    if (hasDatachecksError && data) {
      setIsValidating(true);
      formMethods.handleSubmit(EMPTY_FUNCTION)();
    }
  }, [data, formMethods, hasDatachecksError, message]);

  return {
    formMethods,
    isBigMessage,
    isValidationComplete: hasDatachecksError ? isValidationComplete : true,
  };
};

export const EditDocumentPageUI = ({
  message,
  messageOriginApp,
  data,
  uiConfig,
  relatedTransactions,
  currentPartner,
  processSpecFields,
  currentUser,
}: EditDocumentPageUIProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useFocusErrorOnMount();
  const issueResolver = useIssueResolver(message?.issue_resolver_id);

  const hasOriginalPDF = messageOriginApp === 'DOCUMENT_UNDERSTANDING_AGENT' || messageOriginApp === 'UNATTENDED_OCR';
  const firstAttachment = message?.attachments?.[0];
  const pdfUrl = firstAttachment?.url ?? '';

  const { formMethods, isBigMessage, isValidationComplete } = useEditDocumentForm({
    message,
    data,
    processSpecFields,
  });
  const { handleSubmit, getValues } = formMethods;

  const {
    editMessage,
    isUpdating: isEditingMessage,
    isSuccess,
  } = useEditMessage({
    message,
    processSpecificationFields: processSpecFields,
  });

  const onSubmit = useCallback(() => {
    setConfirmationModalOpen(false);
    editMessage(getValues());
  }, [editMessage, getValues]);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const handleOpenConfirmation = () => {
    setConfirmationModalOpen(true);
  };
  const handleCancelConfirmation = () => setConfirmationModalOpen(false);
  const handleNativeSubmission: FormEventHandler = (e) => {
    e.preventDefault();
    handleOpenConfirmation();
  };

  const isIssueResolver = issueResolver === IssueResolverType.CURRENT_PARTNER;
  const canEditMessage = isIssueResolver || currentUser?.isAdmin;
  const isBannerLoading = !isValidationComplete && isBigMessage;

  return (
    <FormProvider {...formMethods}>
      <Datachecks targetRelationshipId={message?.relationship_id} documentType={message?.type} />
      <Page>
        <Form
          noValidate
          id="webedi-document"
          onSubmit={isBigMessage ? handleNativeSubmission : handleSubmit(handleOpenConfirmation)}
        >
          <ErrorsNavigationContextProvider
            service={WebEDIErrorsServiceSingleton}
            ErrorModalComponent={MessageFieldErrorModal}
          >
            <Page.Head
              rightContent={
                !!message && (
                  <MessageHeader
                    mode={!canEditMessage ? DisplayModes.view : DISPLAY_MODE}
                    message={message}
                    messageOriginApp={null}
                    relatedTransactions={relatedTransactions}
                    currentPartner={currentPartner}
                    isLoading={isEditingMessage}
                  />
                )
              }
            />
            <Page.Section
              rightAside={
                hasOriginalPDF && firstAttachment ? (
                  <PDFReferenceSidebar pdfUrl={pdfUrl} filename={firstAttachment.name ?? ''} />
                ) : undefined
              }
              topBanner={
                message?.blocking_error && !isEditingMessage ? (
                  <WebEDIMessageErrorBanner
                    mode={DISPLAY_MODE}
                    blockingError={message.blocking_error}
                    issueResolver={issueResolver}
                    senderPartner={message.sender_partner}
                    receiverPartner={message.receiver_partner}
                    isCurrentPartnerReceiver={isCurrentPartnerTheMessageReceiver(message, currentPartner?.id)}
                    isLoadingDatachecksErrors={isBannerLoading}
                  />
                ) : null
              }
              bottomBanner={
                canEditMessage && uiConfig ? (
                  <CreateEditDocumentBottomBanner documentType={message?.type as DocumentType} />
                ) : null
              }
            >
              <div className="relative">
                {!canEditMessage ? (
                  <Placeholder
                    title={t('webedi:editDocument.noActionRequired.title')}
                    description={t('webedi:editDocument.noActionRequired.description')}
                    icon={<LockClosedIcon />}
                    ctaText={t('webedi:editDocument.noActionRequired.ctaText')}
                    ctaOnClick={() => {
                      navigate(routeToTransactionPage(TransactionInternalType.message, message?.id as string));
                    }}
                  />
                ) : uiConfig ? (
                  <DocumentSections
                    mode={DISPLAY_MODE}
                    uiConfig={uiConfig}
                    documentType={message?.type as DocumentType}
                    variables={message?.receiver_partner ? { receiverPartnerName: message.receiver_partner.name } : {}}
                    isSubmitting={isEditingMessage}
                    senderPartner={message?.sender_partner}
                    receiverPartner={message?.receiver_partner}
                  />
                ) : null}
              </div>
              <LoadingOverlay isVisible={isEditingMessage} portalRootId={PAGE_SECTION_PORTAL_ROOT_ID} />
            </Page.Section>
          </ErrorsNavigationContextProvider>
        </Form>
      </Page>
      <UnsavedChangesModal shouldBlock={!isSuccess} />
      <SendConfirmationModal
        onConfirm={onSubmit}
        open={confirmationModalOpen}
        onCancel={handleCancelConfirmation}
        documentType={message?.type as DocumentTypeWithVirtual}
        receiverTradePartner={message?.receiver_partner ?? undefined}
      />
    </FormProvider>
  );
};

EditDocument.loader = createLoader(
  ({ params }) => useMessageWithProcessSpecification.getKey({ messageId: params.id }),
  useMessageWithProcessSpecification.queryFn,
);

type ResponseType = Awaited<ReturnType<typeof useMessageWithProcessSpecification.queryFn>>;
EditDocument.handle = createHandle<ResponseType, RouteParams<TransactionsRoutes.edit>>((params, data) => ({
  label: (t: TFunction) =>
    t('webedi:editDocument.breadcrumb', {
      externalReference: data?.data.external_message_ref || EMPTY_FIELD,
      documentType: t(`common:messageTypes.singular.${data?.data.type || DocumentType.unknown}`),
    }),
  to: routeToPage(TransactionsRoutes.edit, { id: params.id }),
  key: `edit-document`,
}));
